import { inject, Injectable } from '@angular/core';
import { from, Observable, of, switchMap, throwError } from 'rxjs';
import {
  CtBatch,
  CtBatchApiService,
  CtBatchLock,
  PsNavigationService,
} from '@ps-gui/claims-terminal-shared';
import { AuthenticationService, PsDialogService } from '@ps-gui/shared-core';
import { UserChangedDialogComponent } from '../domains/dialogs/user-changed-dialog.component';
import { PersistDataService } from './app-state/persist-data.service';

/** Guards continuing to work on a batch, that has been taken over by someone else */
@Injectable({
  providedIn: 'root',
})
export class BatchLockGuardService {
  private readonly ctBatchApiService = inject(CtBatchApiService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly dialogService = inject(PsDialogService);
  private readonly persistDataService = inject(PersistDataService);
  private readonly psNavigationService = inject(PsNavigationService);

  assureStillAssignedToCurrentUser(batch: CtBatch): Observable<void> {
    return from(this.authenticationService.loadUserProfile()).pipe(
      switchMap((profile) => this.ctBatchApiService.checkLockHeldBy(batch.id, profile.id)),
      switchMap(({ stillHeld, lock }) =>
        stillHeld ? of(undefined) : this.handleTakeover(batch, lock),
      ),
    );
  }

  private handleTakeover(batch: CtBatch, lock: CtBatchLock | undefined): Observable<never> {
    this.dialogService
      .open(UserChangedDialogComponent, {
        inputValues: { newUser: lock?.displayName ?? '', ctBatchSource: batch.source },
      })
      .subscribe(() => {
        this.persistDataService
          .reset()
          .finally(() => this.psNavigationService.navigateToOrigin(batch));
      });

    return throwError(
      () =>
        new Error(
          `Batch ${batch.id} is no longer assigned to the current user${
            lock ? ` — taken over by ${lock.displayName ?? lock.userName}` : ''
          }`,
        ),
    );
  }
}
